import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { ShareTemplate } from "../../../configurations/share-messages/types/share-template.types";
import { RootState } from "../../../../store/Index";
import { ContentCopy } from "@mui/icons-material";
import { showSuccessToast } from "../../../../components/common/ShowToast";

interface StreamData {
  deceasedName: string;
  date: string;
  eventName: string;
  time: string;
  serviceType: string;
  venueName: string;
  address: string;
  website_stream_link: string;
  imageUrl: string;
}

interface SocialShareModalProps {
  open: boolean;
  onClose: () => void;
  streamData: StreamData;
  platform: "facebook" | "whatsapp";
  selectedTemplate?: ShareTemplate | null;
}

const SocialShareModal: React.FC<SocialShareModalProps> = ({
  open,
  onClose,
  streamData,
  platform,
  selectedTemplate,
}) => {
  const theme = useSelector((state: RootState) => state.theme);
  const [customMessage, setCustomMessage] = useState("");

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(finalMessage);
    setCopied(true);
    showSuccessToast("Message Copied succesfully");
    setTimeout(() => setCopied(false), 1500);
  };

  const finalMessage = useMemo(() => {
    if (customMessage) {
      return customMessage;
    }

    const bold = (text: string) => `*${text}*`;
    if (!selectedTemplate) {
      return `${bold(streamData.eventName || streamData.deceasedName)}\n\n${bold(
        streamData.date,
      )}\n${bold(streamData.time)}\n\n${bold(
        streamData.address,
      )}\n\nWatch here: ${bold(streamData.website_stream_link)}`;
    }

    let message = selectedTemplate.body_template;
    message = message.replace(
      /{deceasedName}/g,
      bold(streamData.deceasedName || ""),
    );
    message = message.replace(/{eventName}/g, bold(streamData.eventName || ""));
    message = message.replace(/{date}/g, bold(streamData.date || ""));
    message = message.replace(/{time}/g, bold(streamData.time || ""));
    message = message.replace(/{venue}/g, bold(streamData.venueName || ""));
    message = message.replace(/{address}/g, bold(streamData.address || ""));
    message = message.replace(
      /{website_stream_link}/g,
      bold(streamData.website_stream_link || ""),
    );

    return message;
  }, [customMessage, selectedTemplate, streamData]);

  const handleShare = () => {
    try {
      const shareUrl =
        platform === "facebook"
          ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              streamData.website_stream_link,
            )}`
          : `https://wa.me/?text=${encodeURIComponent(finalMessage)}`;

      window.open(shareUrl, "_blank");

      onClose();
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "50vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.primary_colors[900],
          color: theme.primary_colors[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Share on {platform === "facebook" ? "Facebook" : "WhatsApp"}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {copied && (
            <Typography
              variant="body2"
              sx={{ color: theme.primary_colors[300] }}
            >
              Copied!
            </Typography>
          )}

          <Tooltip title="Copy message">
            <IconButton
              onClick={handleCopy}
              sx={{ color: theme.primary_colors[100] }}
            >
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: theme.primary_colors[900],
          pt: 3,
          minHeight: "60vh",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: theme.primary_colors[300], mb: 2 }}
        >
          Message Preview:
        </Typography>

        {/* Message TextField */}
        <TextField
          fullWidth
          multiline
          rows={15}
          value={finalMessage}
          onChange={(e) => setCustomMessage(e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              color: theme.primary_colors[100],
              fontFamily: "monospace",
              fontSize: "0.85rem",
            },
            "& .MuiInputLabel-root": { color: theme.primary_colors[300] },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: theme.primary_colors[900] }}>
        <Button onClick={onClose} sx={{ color: theme.primary_colors[300] }}>
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          sx={{
            backgroundColor: platform === "facebook" ? "#1877F2" : "#25d366",
            color: theme.primary_colors[900],
          }}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SocialShareModal;
